import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../api/store";

interface TransferOwnershipUser {
    id: number | null;
    name: string;
    email: string;
}

export interface SelectedProposal {
    id: number;
    groupName: string;
}

export interface TransferOwnershipInitial {
    isTransferFinal: boolean;
    isShowUserList: null | 'currentOwner' | 'newOwner'
    transferOwnership: {
        currentOwner: TransferOwnershipUser;
        newOwner: TransferOwnershipUser;
        selectProposal: SelectedProposal[];
    };
    searchValue: string;
}

const initialState: TransferOwnershipInitial = {
    isTransferFinal: false,
    isShowUserList: null,
    transferOwnership: {
        currentOwner: {
            id: 0,
            name: "Select current owner",
            email: "",
        },
        newOwner: {
            id: 0,
            name: "Select new owner",
            email: "",
        },
        selectProposal: [],
    },
    searchValue: ''
};

const transFerOwnershipSlice = createSlice({
    name: "transFerOwnershipSlice",
    initialState,
    reducers: {
        setIsTransferFinal: (state, action: PayloadAction<boolean>) => {
            state.isTransferFinal = action.payload;
        },
        setShowUserList: (state, action: PayloadAction<null | 'currentOwner' | 'newOwner'>) => {
            state.isShowUserList = action.payload;
        },
        setCurrentOwner: (state, action: PayloadAction<TransferOwnershipUser>) => {
            state.transferOwnership.currentOwner = action.payload;
        },
        setNewOwner: ( state, action: PayloadAction<TransferOwnershipUser> ) => {
            state.transferOwnership.newOwner = action.payload;
        },

        setSelectedProposals: (state, action: PayloadAction<SelectedProposal[] | SelectedProposal>) => {
            const proposals = Array.isArray(action.payload)
                ? action.payload
                : [action.payload];

            const selected = state.transferOwnership.selectProposal;
            const allSelected = proposals.every((proposal) =>
                selected.some((item) => item.id === proposal.id)
            );

            if (allSelected) {
                const idsToRemove = new Set(proposals.map((proposal) => proposal.id));

                state.transferOwnership.selectProposal = selected.filter(
                    (item) => !idsToRemove.has(item.id)
                );
            } else {
                const existingIds = new Set(selected.map((item) => item.id));

                state.transferOwnership.selectProposal.push(
                    ...proposals.filter((proposal) => !existingIds.has(proposal.id))
                );
            }
        },

        addSelectedProposal: (state, action: PayloadAction<SelectedProposal>) => {
            const exists = state.transferOwnership.selectProposal.some(
                (proposal) => proposal.id === action.payload.id
            );

            if (!exists) {
                state.transferOwnership.selectProposal.push(action.payload);
            }
        },

        removeSelectedProposal: (state, action: PayloadAction<number>) => {
            state.transferOwnership.selectProposal =
                state.transferOwnership.selectProposal.filter(
                    (proposal) => proposal.id !== action.payload
                );
        },

        clearSelectedProposals: (state) => {
            state.transferOwnership.selectProposal = [];
        },

        setSearchOwnershipProposal: (state, action: PayloadAction<string>) => {
            state.searchValue = action.payload;
        },

        clearTransferOwnershipState: () => initialState,
    },
});

export const {
    setIsTransferFinal,
    setShowUserList,
    setCurrentOwner,
    setNewOwner,
    setSelectedProposals,
    addSelectedProposal,
    removeSelectedProposal,
    clearSelectedProposals,
    setSearchOwnershipProposal,
    clearTransferOwnershipState,
} = transFerOwnershipSlice.actions;

export default transFerOwnershipSlice.reducer;

export const selectIsTransferFinal = (state: RootState) => state.transFerOwnershipSlice.isTransferFinal;
export const selectTransferOwnership = (state: RootState) => state.transFerOwnershipSlice.transferOwnership;
export const selectCurrentOwner = (state: RootState) => state.transFerOwnershipSlice.transferOwnership.currentOwner;
export const selectNewOwner = (state: RootState) => state.transFerOwnershipSlice.transferOwnership.newOwner;
export const selectSelectedProposals = (state: RootState) => state.transFerOwnershipSlice.transferOwnership.selectProposal;
export const selectIsShowUserList = (state: RootState) => state.transFerOwnershipSlice.isShowUserList;
export const selectSearchValue = (state: RootState) => state.transFerOwnershipSlice.searchValue;